/**
 * Loader for the admin-managed Resource Code + Resource Code Mapping tables.
 *
 * Both tables live in Forecast_User_Data under BU_ID='all' and are
 * scoped to the current version GUID. They are uploaded on the
 * Admin → Resource Code tab.
 *
 * Returns:
 *   byCode            – { CODE: { description, costType } }
 *   codesByCostType   – { costType: { CODE: description } }
 *   mappingByCode     – { CODE: { code2, description2, costType } }
 */

import { loadUserData } from '../db/company';

const norm = v => (v == null ? '' : String(v).trim());

let cache = { key: '', data: null };

export const loadResourceCodeTables = async ({ forecastGuid }) => {
    if (!forecastGuid) return { byCode: {}, codesByCostType: {}, mappingByCode: {} };
    if (cache.key === forecastGuid && cache.data) return cache.data;

    const result = await loadUserData({ forecastGuid, id: 'all' });
    const byCode = {};
    const codesByCostType = {};
    const mappingByCode = {};

    (result?.data || []).forEach(d => {
        if (d?.BU_ID !== 'all') return;
        const code = norm(d.Forecast_Value);
        const ct = norm(d.Cost_Type);
        const desc = norm(d.Description);
        if (!code) return;

        if (d.Forecast_Tab === 'Resource Code') {
            byCode[code] = { description: desc, costType: ct };
            if (ct) {
                if (!codesByCostType[ct]) codesByCostType[ct] = {};
                codesByCostType[ct][code] = desc;
            }
        } else if (d.Forecast_Tab === 'Resource Code Mapping') {
            mappingByCode[code] = {
                code2: norm(d.Resource_Code),
                description2: norm(d.Supplier_Name),
                description: desc,
                costType: ct,
            };
        }
    });

    const data = { byCode, codesByCostType, mappingByCode };
    cache = { key: forecastGuid, data };
    return data;
};

/**
 * Build the ETC-modal dropdown { code: description } filtered to the
 * given allowed cost types. If the DB tables are empty, falls back to the
 * provided `fallback` map (e.g. the static Materials reference list).
 */
export const buildResourceDropdownFromDb = ({
    byCode = {},
    codesByCostType = {},
    allowedCostTypes = [],
    projectResources = {},
    budgetByResource = {},
    fallback = null,
}) => {
    const hasDb = Object.keys(byCode).length > 0;
    if (!hasDb && fallback) return fallback;

    const valid = {};
    if (allowedCostTypes && allowedCostTypes.length) {
        allowedCostTypes.forEach(ct => {
            const codes = codesByCostType[ct] || {};
            Object.entries(codes).forEach(([code, desc]) => { valid[code] = desc; });
        });
    } else {
        Object.entries(byCode).forEach(([code, { description }]) => { valid[code] = description; });
    }

    // Restrict to codes that actually have a value in this project,
    // but preserve the DB description.
    const used = new Set();
    Object.entries(budgetByResource).forEach(([code, amt]) => {
        if (code && +amt > 0) used.add(code);
    });
    Object.keys(projectResources).forEach(code => { if (code) used.add(code); });

    const out = {};
    used.forEach(code => {
        if (valid[code] != null) out[code] = valid[code];
    });
    // If the project has no matching rows yet, show the full valid set
    // so a user can pick from it when creating the first entry.
    if (Object.keys(out).length === 0) {
        Object.assign(out, valid);
    }
    return out;
};

export const clearResourceCodeCache = () => { cache = { key: '', data: null }; };
